import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import {
  fetchCompanyPricing,
  fetchCompanySubscription,
  selectCompanyInfo,
} from "store/companySlice";
import { clearCurrentBoard } from "store/currentBoardSlice";

import Button from "layout/atoms/Button";
import ContentBox from "layout/atoms/ContentBox";
import Dropdown from "layout/atoms/Dropdown";
import GridTwoThird from "layout/atoms/GridTwoThird";
import Header from "layout/atoms/Header";
import Label from "layout/atoms/Label";
import Link from "layout/atoms/Link";
import MainContainer from "layout/atoms/MainContainer";
import Filters from "layout/molecules/Filters";

import PriceBox from "layout/atoms/PriceBox";
import PricingTabs from "layout/molecules/PricingTabs";

import GridBoardList from "layout/atoms/GridBoardList";
import { openModal } from "store/modalsSlice";

function Subscription(props) {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const companyInfo = useSelector(selectCompanyInfo);
  const [paymentPeriod, setPaymentPeriod] = useState(1);
  const [pickedPlan, setPickedPlan] = useState(0);

  useEffect(() => {
    dispatch(clearCurrentBoard());
    companyInfo.id && dispatch(fetchCompanySubscription(companyInfo.id));
    companyInfo.id && dispatch(fetchCompanyPricing(companyInfo.id));
  }, [dispatch, companyInfo.id]);

  const handlePlanClick = (plan) => {
    setPickedPlan({ product: plan.product, ...plan.options[paymentPeriod] });
  };

  return (
    <MainContainer
      topbar={
        <div>
          <h2>{t("subscription.header")}</h2>
        </div>
      }
    >
      <GridTwoThird>
        <div>
          {companyInfo.subscription && (
            <ContentBox>
              <Header size="h2" margin="m">
                {t(`subscription.currentPlan.header`)}:{" "}
                <span>
                  {t(`subscription.${companyInfo.subscription.product}.header`)}
                </span>
              </Header>
              <br />
              <GridBoardList>
                {companyInfo.subscription.product !== "FREE" && (
                  <Header size="h5" margin="xs">
                    {t("subscription.validUntil")}{" "}
                    <span>
                      {new Date(companyInfo.subscription.expiresAt)
                        .toISOString()
                        .split("T")[0]
                        .replace(/-/g, "/")}
                    </span>
                  </Header>
                )}
                <Header size="h5" margin="xs">
                  {t("subscription.activeBoards")}{" "}
                  <span>
                    {companyInfo.subscription.usage.activeBoards.cap < 500
                      ? `${companyInfo.subscription.usage.activeBoards.used} / ${companyInfo.subscription.usage.activeBoards.cap}`
                      : "∞"}
                  </span>
                </Header>
                <Header size="h5" margin="xs">
                  {t("subscription.ideas")}{" "}
                  <span>
                    {companyInfo.subscription.usage.ideas
                      ? `${companyInfo.subscription.usage.ideas.used} / ${companyInfo.subscription.usage.ideas.cap}`
                      : "∞"}
                  </span>
                </Header>
              </GridBoardList>
            </ContentBox>
          )}
          <ContentBox
            footer={
              <>
                <Link underline>
                  <a
                    href="https://ideolo.co/pricing"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    {t("subscription.fullComparison")}
                  </a>
                </Link>
                <Button
                  type="submit"
                  size="lg"
                  color="primary"
                  disabled={pickedPlan === 0}
                  onClick={() =>
                    dispatch(
                      openModal({
                        name: "checkoutModal",
                        data: { pickedPlan },
                      }),
                    )
                  }
                >
                  {t("buttons.continue")}
                </Button>
              </>
            }
          >
            <Header size="h2" margin="m">
              {t(`subscription.pickPlan.header`)}
            </Header>
            <Filters
              billed={
                <Dropdown
                  placement="bottom-start"
                  trigger={
                    <strong>
                      {paymentPeriod === 0 ? t("monthly") : t("yearly")} &#9662;
                    </strong>
                  }
                >
                  <Label
                    size="s"
                    status={t("monthly")}
                    onClick={() => {
                      setPickedPlan(0);
                      setPaymentPeriod(0);
                    }}
                  >
                    {t(`statuses.monthly`)}
                  </Label>
                  <Label
                    size="s"
                    status={t("yearly")}
                    onClick={() => {
                      setPickedPlan(0);
                      setPaymentPeriod(1);
                    }}
                  >
                    {t(`statuses.yearly`)}
                  </Label>
                </Dropdown>
              }
            />
            <br />
            {companyInfo.pricing &&
              companyInfo.pricing[0].options[paymentPeriod].discountedPrice && (
                <>
                  <Label size="s" status="complete">
                    {t(`subscription.discountCode`)}
                  </Label>
                  &nbsp;&nbsp;
                </>
              )}

            {paymentPeriod === 1 ? (
              <Label size="s" status="complete">
                {t(`subscription.save`)}
              </Label>
            ) : (
              <Label
                size="s"
                status="in_progress"
                onClick={() => setPaymentPeriod(1)}
              >
                {t(`subscription.saveAlert`)}
              </Label>
            )}
            <PricingTabs>
              {companyInfo.pricing &&
                companyInfo.pricing.map((plan) => {
                  const choosenOption = plan.options[paymentPeriod]
                    .discountedPrice
                    ? plan.options[paymentPeriod].discountedPrice
                    : plan.options[paymentPeriod].price;
                  const planPrice =
                    paymentPeriod === 1
                      ? choosenOption.amountMinor / 1200
                      : choosenOption.amountMinor / 100;

                  return (
                    <PriceBox
                      key={plan.product}
                      currentPlan={
                        companyInfo.subscription
                          ? companyInfo.subscription.product
                          : "FREE"
                      }
                      picked={plan.product === pickedPlan.product}
                      name={plan.product}
                      months={plan.options[paymentPeriod].months}
                      price={planPrice}
                      hasDiscount={
                        plan.options[paymentPeriod].discountedPrice
                          ? true
                          : false
                      }
                      strikedPrice={plan.options[0].price.amountMinor / 100}
                      onClick={() => handlePlanClick(plan)}
                    />
                  );
                })}
            </PricingTabs>
          </ContentBox>
          <div
            style={{
              marginTop: "20px",
              textAlign: "start",
              fontSize: "13px",
              color: "#666",
            }}
          >
            {t("subscription.invoiceBox.text")}{" "}
            <a
              href={`mailto:invoice@ideolo.co?subject=${companyInfo.name} - Invoice request&body=All you have to do is just send this email. We'll reach you within 12h. Please, don't change the subject.`}
              style={{ color: "#666", textDecoration: "underline" }}
            >
              {t("subscription.invoiceBox.header")}
            </a>
          </div>
        </div>
        <div>
          {/* <ContentBox>
            <Header size="h3" margin="m">
              {t("subscription.history.header")}
            </Header>
            <Table>
              <thead>
                <tr>
                  <td>Date</td>
                  <td>Plan</td>
                  <td>Cost</td>
                  <td>Invoice</td>
                </tr>
              </thead>
              <tbody>
                {[...Array(30)].map(() => (
                  <tr>
                    <td>9 Mar 23</td>
                    <td>Monthly subscription</td>
                    <td>20$</td>
                    <td>
                      <Button size="" color="white">
                        <Icon src={InvoiceSvg} />
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </ContentBox> */}
        </div>
      </GridTwoThird>
    </MainContainer>
  );
}

export default Subscription;
